import React, { useState, useEffect } from 'react';
import { Mail, Loader2, AlertCircle, CheckCircle, X } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function mapError(err) {
  switch (err?.code) {
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
      return 'Password non corretta';
    case 'auth/email-already-in-use':
      return 'Questa email è già associata a un altro account';
    case 'auth/invalid-email':
      return 'Formato email non valido';
    case 'auth/requires-recent-login':
      return 'Rieffettua il login e riprova';
    case 'auth/too-many-requests':
      return 'Troppi tentativi. Riprova tra qualche minuto.';
    case 'auth/network-request-failed':
      return 'Errore di rete. Controlla la connessione.';
    default:
      return 'Errore durante l\'aggiornamento. Riprova.';
  }
}

export default function ChangeEmailModal({ isOpen, onClose, darkMode = false, currentEmail }) {
  const { changeEmail } = useAuth();

  const [newEmail, setNewEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setNewEmail('');
      setPassword('');
      setLoading(false);
      setError(null);
      setSuccess(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const trimmed = newEmail.trim();

    if (!EMAIL_REGEX.test(trimmed)) {
      setError('Formato email non valido');
      return;
    }
    if (trimmed.toLowerCase() === (currentEmail || '').toLowerCase()) {
      setError('La nuova email è uguale a quella attuale');
      return;
    }
    if (!password) {
      setError('Inserisci la password attuale');
      return;
    }

    setLoading(true);
    try {
      await changeEmail(trimmed, password);
      setSuccess(true);
    } catch (err) {
      setError(mapError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className={`w-full max-w-md rounded-2xl border shadow-xl ${
        darkMode
          ? 'bg-neutral-900 border-neutral-700 text-white'
          : 'bg-white border-neutral-200 text-neutral-900'
      }`}>
        <div className={`flex items-center justify-between px-5 py-4 border-b ${
          darkMode ? 'border-neutral-700' : 'border-neutral-200'
        }`}>
          <div className="flex items-center gap-2">
            <Mail className={`w-5 h-5 ${darkMode ? 'text-forfy-400' : 'text-forfy-600'}`} />
            <h2 className="text-base font-semibold">Modifica email</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            aria-label="Chiudi"
            className={`p-1 rounded-lg transition-colors ${
              darkMode ? 'hover:bg-neutral-800 text-neutral-400' : 'hover:bg-neutral-100 text-neutral-500'
            } disabled:opacity-50`}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {success ? (
          <div className="p-5 space-y-4">
            <div className="flex items-start gap-3">
              <CheckCircle className={`w-5 h-5 shrink-0 mt-0.5 ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
              <div>
                <h3 className="text-sm font-semibold">Verifica inviata</h3>
                <p className={`mt-1 text-sm ${darkMode ? 'text-neutral-400' : 'text-neutral-600'}`}>
                  Abbiamo inviato un'email di verifica a <b>{newEmail.trim()}</b>. Apri la mail e clicca sul link per confermare il cambio.
                </p>
                <p className={`mt-2 text-xs ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>
                  Fino a quando non confermi, continuerai ad accedere con <b>{currentEmail}</b>.
                </p>
              </div>
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-lg bg-forfy-500 text-white text-sm hover:bg-forfy-600"
              >
                Ho capito
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-5 space-y-4">
            <div>
              <label className={`text-xs ${darkMode ? 'text-neutral-500' : 'text-neutral-600'}`}>
                Email attuale
              </label>
              <div className={`mt-1 px-3 py-2 rounded-lg text-sm ${
                darkMode ? 'bg-neutral-800 text-neutral-400' : 'bg-neutral-50 text-neutral-600'
              }`}>
                {currentEmail || '—'}
              </div>
            </div>

            <div>
              <label htmlFor="new-email" className={`text-xs ${darkMode ? 'text-neutral-500' : 'text-neutral-600'}`}>
                Nuova email
              </label>
              <input
                id="new-email"
                type="email"
                autoComplete="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                disabled={loading}
                placeholder="nuova@email.it"
                className={`mt-1 w-full px-3 py-2 border rounded-lg text-sm ${
                  darkMode
                    ? 'border-neutral-700 bg-neutral-800 text-white placeholder:text-neutral-600'
                    : 'border-neutral-200 bg-white text-neutral-900 placeholder:text-neutral-400'
                } disabled:opacity-50`}
              />
            </div>

            <div>
              <label htmlFor="current-password" className={`text-xs ${darkMode ? 'text-neutral-500' : 'text-neutral-600'}`}>
                Password attuale
              </label>
              <input
                id="current-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                placeholder="••••••••"
                className={`mt-1 w-full px-3 py-2 border rounded-lg text-sm ${
                  darkMode
                    ? 'border-neutral-700 bg-neutral-800 text-white placeholder:text-neutral-600'
                    : 'border-neutral-200 bg-white text-neutral-900 placeholder:text-neutral-400'
                } disabled:opacity-50`}
              />
              <p className={`mt-1 text-xs ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>
                Richiesta per confermare la tua identità.
              </p>
            </div>

            {error && (
              <div className={`flex items-start gap-2 px-3 py-2 rounded-lg text-sm ${
                darkMode ? 'bg-red-900/20 text-red-400' : 'bg-red-50 text-red-600'
              }`}>
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className={`px-4 py-2 rounded-lg border text-sm ${
                  darkMode
                    ? 'border-neutral-700 bg-neutral-800 text-neutral-300 hover:bg-neutral-700'
                    : 'border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50'
                } disabled:opacity-50`}
              >
                Annulla
              </button>
              <button
                type="submit"
                disabled={loading || !newEmail.trim() || !password}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-forfy-500 text-white text-sm hover:bg-forfy-600 disabled:opacity-50"
              >
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                {loading ? 'Invio in corso...' : 'Invia verifica'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
